"use client";

import { ModalWrapper } from "@2u-landing-pages/components/Modal";
import { PageStateProvider, PricingApiContextProvider, ProductCardHighlightProvider, ProgramMetaProvider } from "@2u-landing-pages/providers";
import { ModalProvider } from "@2u-landing-pages/providers/ModalProvider";
import { Drawer } from "@2u-landing-pages/templates/Drawer";
import { Footer } from "@2u-landing-pages/templates/Footer";
import { Form } from "@2u-landing-pages/templates/Form";
import { StickyElement } from "@2u-landing-pages/templates/StickyElement";
import {
  TypeProgram_metaWithoutUnresolvableLinksResponse,
  TypeProgram_variationWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { getDapiRandomId } from "@2u-landing-pages/utils/dapi-cookie";
import { pushCohesionAnonIdToSegment, validateAnonId } from "@2u-landing-pages/utils/validate-anon-id";
import eventClient from "@2u-landing-pages/utils/event-client";
import { formatPhoneNumber } from "@2u-landing-pages/utils/formatting";
import { useDAPI } from "@2u-landing-pages/utils/useDAPI";
import {
  ContentfulLivePreviewProvider,
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from "@contentful/live-preview/react";
import { type BasePageParams } from "@lib/generateParams";
import { ErrorBoundary } from "@2u-landing-pages/components/ErrorHandler";
import { logCustomAttributes, logEvent } from "@red-digital/bricks";
import resolveResponse from "contentful-resolve-response";
import React, { useEffect, useRef, useState } from "react";
import { thankYouPages } from "../constants/thankYouPages";
import { PreviewMode } from "./PreviewMode";
import { Sections } from "./Sections";

interface PageProps {
  preview?: boolean;
  program: any;
  school: any;
  internal?: boolean;
  params?: BasePageParams;
  writeKey?: string;
  /**
   * Server-rendered LpFooter. Supplied only for schools that have a
   * footer_config; every other school keeps the program-level Footer.
   * Passed as a slot because this component is a client boundary and
   * LpFooter's data has to be resolved on the server.
   */
  footerSlot?: React.ReactNode;
}

function getThankYouPagePath(params: BasePageParams) {
  const { lob, school, program } = params;
  const envCfg = thankYouPages[process.env.NEXT_ENV as keyof typeof thankYouPages] || thankYouPages.development;

  const lobCfg = envCfg?.[lob as keyof typeof envCfg] || {};
  const schoolCfg = lobCfg?.[school as keyof typeof lobCfg] || {};
  const typ = schoolCfg?.[program as keyof typeof schoolCfg] || "";

  return typ;
}

function setCookie(key: string, decision: string, path: string = "/") {
  const domain = window.location.hostname.replace("info.", "");
  const d = new Date();
  d.setDate(d.getDate() + 7);
  document.cookie = `${key}=${decision}; Domain=${domain}; Path=${path}; Expires=${d.toUTCString()}; Secure`;
}

const Page: React.FC<PageProps> = ({ program, school, preview, internal, params, writeKey = "", footerSlot }) => {
  const disableOpacity = program?.fields?.disable_monarch_opacity;
  const [isLoading, setIsLoading] = useState<boolean>(!preview && !disableOpacity);
  const [variation, setVariation] = useState(program?.fields?.base_variation);
  const livePreviewVariation = useContentfulLiveUpdates(variation);
  const inspector = useContentfulInspectorMode();
  const activeVariation = preview ? livePreviewVariation : variation;
  const { sections, drawer, logo_primary, sticky_element } = activeVariation?.fields || {};
  const decsionRendered = useRef(false);
  const manualDecisionTriggered = useRef(false);
  const phoneNumber = formatPhoneNumber(program?.fields?.phone_number);

  // Initialize DAPI hook - generates preconsent ID immediately (only for degrees)
  const isDegreeProgram = params?.lob === "degree";
  const { fireDAPIEvent } = useDAPI({
    enabled: !preview && isDegreeProgram, // Only enable for degrees in non-preview mode
  });

  // TODO(EDUN-13396): remove after Core DS mapping (P1-T3b). Flip to true to prefer tenant-config.
  const allowTenantConfigStylesheet = false;
  const tenantConfigId = school?.fields?.tenant_config_id;
  const contentfulPrimaryVars =
    allowTenantConfigStylesheet && tenantConfigId
      ? ""
      : `
            --primary-1: ${school?.fields?.primary_1?.value};
            --primary-2: ${school?.fields?.primary_2?.value};
            --primary-3: ${school?.fields?.primary_3?.value};
            --primary-4: ${school?.fields?.primary_4?.value};
`;

  const variationName = useRef(variation?.fields?.name);
  useEffect(() => {
    variationName.current = variation?.fields?.name;
  }, [variation]);

  useEffect(() => {
    if (preview) return;

    if (window.cohesion) {
      window.cohesion("monarch:done", function (err: object | null, result: any) {
        // Prevents monarch:done from being called multiple times
        if (decsionRendered.current) return;

        const durationMs = window.__monarchRequestStart
          ? Math.round(performance.now() - window.__monarchRequestStart)
          : undefined;

        if (window?.newrelic?.addPageAction && durationMs !== undefined) {
          window.newrelic.addPageAction("monarch_request_timing", {
            durationMs,
            result: err ? "error" : "success",
            pageType: "programs",
          });
        }

        logEvent("monarch_request_done", {
          result: err ? "error" : "success",
        });

        if (err) {
          console.log("Monarch Error", err);
          setIsLoading(false);

          eventClient.tempMonarchDecision({
            webElement: {
              location: "Monarch Error",
              name: program?.fields?.base_variation?.fields?.name || "",
              htmlId: program?.fields?.base_variation?.sys?.id || "",
              text: JSON.stringify(err),
            },
          });

          logEvent("monarch_error", {
            error: JSON.stringify(err),
          });

          return;
        }

        let resolvedContent: TypeProgram_variationWithoutUnresolvableLinksResponse | undefined;
        const monarchVariation = result?.source?.[school?.fields?.slug]
          ?.value as TypeProgram_variationWithoutUnresolvableLinksResponse;

        if (monarchVariation?.sys) {
          resolvedContent = resolveResponse(monarchVariation)?.[0];
          if (resolvedContent) {
            setVariation(resolvedContent);

            logEvent("monarch_decision", {
              variation: resolvedContent?.fields?.name || "",
              sysId: resolvedContent?.sys?.id || "",
            });
          }
        }

        eventClient.tempMonarchDecision({
          webElement: {
            location: "Decision Rendered",
            name: resolvedContent?.fields?.name || program?.fields?.base_variation?.fields?.name,
            htmlId: resolvedContent?.sys?.id || program?.fields?.base_variation?.sys?.id,
          },
        });

        const thankYouPagePath =
          program?.fields?.thank_you_page_path || getThankYouPagePath(params || { school: "", program: "", lob: "" });

        if (thankYouPagePath) {
          const two_u_redirect = String(Math.floor(Math.random() * 100) + 1);
          setCookie("two_u_redirect", two_u_redirect, thankYouPagePath);

          eventClient.thankYouPageDecision({
            redirectCookie: two_u_redirect,
          });
        }

        // Prevents monarch:done from being called multiple times
        decsionRendered.current = true;

        setIsLoading(false);
      });

      window.cohesion("ready", () => {
        const sessionId = window?._Cohesion?.sessionId || "";
        logCustomAttributes([{ name: "ClientSessionId", value: sessionId || "" }]);
        if (window?.newrelic) {
          window?.newrelic?.setUserId(sessionId);
        }
        pushCohesionAnonIdToSegment();
      });

      window.cohesion("consent:changed", function (groups) {
        const consentCaptured = Boolean(groups?.find((group: string) => group === "C0002"));

        if (consentCaptured) {
          pushCohesionAnonIdToSegment();
          validateAnonId();
          logEvent("consent_captured", {
            consentCaptured,
            groups: groups.join(", "),
          });

          if (isDegreeProgram) {
            fireDAPIEvent();
          }
        }
      });

      window.cohesion("error", () => {
        eventClient.tempMonarchDecision({
          webElement: {
            location: "Cohesion Error",
            name: program?.fields?.base_variation?.fields?.name || "",
            htmlId: program?.fields?.base_variation?.sys?.id || "",
          },
        });

        setIsLoading(false);
      });

      window.cohesion("tagular:eventSucceeded", (event: any) => {
        if (window.monarch && event?.event === "core.redventures.usertracking.ConsentCaptured.v1") {
          if (manualDecisionTriggered.current || !event?.properties?.webContext?.cohesionConsent) return;

          // Get dapiRandomId from utility function
          const currentDapiId = getDapiRandomId();
          window.monarch(
            "rule",
            school.fields.slug,
            {
              application: "rv_creative",
              callType: "manual",
              experienceServed: variationName.current,
              dapiRandomId: currentDapiId,
            },
            {
              writeKey,
            },
            () => null
          );
          manualDecisionTriggered.current = true;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  return (
    <ContentfulLivePreviewProvider locale="en-US" enableInspectorMode={true} enableLiveUpdates={true}>
      <style>
        {`
          :root {
            ${contentfulPrimaryVars}
            --action-button-primary-active: var(${activeVariation?.fields?.primary_button_color});
            --action-button-primary-default: var(${activeVariation?.fields?.primary_button_color});
            --action-button-primary-focus: var(${activeVariation?.fields?.primary_button_color});
            --action-button-primary-hover: var(${activeVariation?.fields?.primary_button_color});
            --action-button-primary-light-active: var(${activeVariation?.fields?.primary_button_color});
            --action-button-primary-light-default: var(${activeVariation?.fields?.primary_button_color});
            --action-button-primary-light-focus: var(${activeVariation?.fields?.primary_button_color});
            --action-button-primary-light-hover: var(${activeVariation?.fields?.primary_button_color});

            --action-button-secondary-active: var(${activeVariation?.fields?.secondary_button_color});
            --action-button-secondary-hover: var(${activeVariation?.fields?.secondary_button_color});
            --action-button-secondary-inverse: var(${activeVariation?.fields?.secondary_button_color});
            --action-button-secondary-light-active: var(${activeVariation?.fields?.secondary_button_color});
            --action-button-secondary-light-hover: var(${activeVariation?.fields?.secondary_button_color});
            --action-button-secondary-light-inverse: var(${activeVariation?.fields?.secondary_button_color});

            --footer-text-color: var(${activeVariation?.fields?.footer_text_color});
            --footer-background-color: var(${activeVariation?.fields?.footer_background_color});
          }

          body {
            font-family: var(--font-family-sans-serif);
          }
          .page {
            display: flex;
            flex-direction: column;
            min-height: 100vh;
          }
          ${
            activeVariation?.fields?.serif_headings
              ? `
            h1, h2, h3, h4, h5, h6 {
              --font-family-primary: var(--font-family-serif);
            }
          `
              : ""
          }
        `}
      </style>
      <div className="page" data-has-drawer={Boolean(drawer) || undefined} style={{ opacity: isLoading ? 0 : 1 }}>
        <PageStateProvider primaryButtonDarkLabel={activeVariation?.fields?.primary_button_dark_label}>
          <ProductCardHighlightProvider>
          <ErrorBoundary title="Uh-oh! An Unexpected Issue Occurred" production={process.env.NEXT_ENV === "production"}>
            {preview && <PreviewMode internal={internal} />}
            <ProgramMetaProvider
              programMeta={program?.fields?.program_meta as TypeProgram_metaWithoutUnresolvableLinksResponse}
            >
            <PricingApiContextProvider
              programMeta={program?.fields?.program_meta as TypeProgram_metaWithoutUnresolvableLinksResponse}
            >
              <ModalProvider modals={activeVariation?.fields?.modal || []}>
                <Sections
                  sections={sections}
                  program={program}
                  phoneNumber={phoneNumber}
                  school={school}
                  programLogo={logo_primary}
                  isLoading={isLoading}
                />
                {drawer ? (
                  <Drawer
                    {...drawer}
                    inspector={inspector}
                    isLoading={isLoading}
                    phoneNumber={phoneNumber}
                    programVariationName={activeVariation?.fields?.name}
                  />
                ) : (
                  <Drawer
                    isLoading={isLoading}
                    phoneNumber={phoneNumber}
                    programVariationName={activeVariation?.fields?.name}
                  >
                    <Form isLoading={isLoading} inDrawer mode="single-program" />
                  </Drawer>
                )}
                {sticky_element && (
                  <StickyElement
                    id={sticky_element.sys.id}
                    {...sticky_element.fields}
                    phoneNumber={phoneNumber}
                  />
                )}
                {footerSlot ?? <Footer variation={activeVariation} program={program} />}
                <ModalWrapper
                  school={school}
                  phoneNumber={phoneNumber}
                  programLogo={logo_primary}
                  inspector={inspector}
                  isLoading={isLoading}
                />
              </ModalProvider>
            </PricingApiContextProvider>
            </ProgramMetaProvider>
          </ErrorBoundary>
          </ProductCardHighlightProvider>
        </PageStateProvider>
      </div>
    </ContentfulLivePreviewProvider>
  );
};

export default Page;
